import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { SocketEvent, DiscordUser } from '@swtorbit/shared-socket-events';
import { useDiscordContext } from '../contexts/DiscordContext';
import { authLogger } from '../utils/logger';
import { Modal } from '@swtorbit/ui/react';

interface DiscordAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Storage key for the expected OAuth state (CSRF protection)
const OAUTH_STATE_KEY = 'discord-oauth-expected-state';

export function DiscordAuthModal({ isOpen, onClose }: DiscordAuthModalProps) {
  const { socket } = useSocketContext();
  const { isLinked, discordUser, setDiscordLinked, linkDiscord } = useDiscordContext();
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  
  // Store the expected state for validation
  const expectedStateRef = useRef<string | null>(null);
  
  // Track success timeout for cleanup
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Track poll interval for cleanup
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Cleanup success timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  // Socket event listeners for Discord auth
  useEffect(() => {
    if (!socket) return;

    const handleAuthUrl = (data: { url: string; state?: string; error?: string }) => {
      if (data.error || !data.url) {
        setLinkError(data.error || 'Failed to get Discord auth URL');
        return;
      }
      
      // Store the expected state for CSRF validation
      if (data.state) {
        expectedStateRef.current = data.state;
        sessionStorage.setItem(OAUTH_STATE_KEY, data.state);
        authLogger.debug('Stored expected OAuth state for CSRF validation');
      } else {
        authLogger.warn('Server did not provide OAuth state - CSRF protection disabled');
      }
      
      window.open(data.url, 'discord-auth', 'width=500,height=800');
      setIsLinking(true);
    };

    const handleLinkComplete = (data: { success: boolean; user?: DiscordUser; error?: string }) => {
      setIsLinking(false);
      if (data.success && data.user) {
        setDiscordLinked(true, data.user);
        setLinkError(null);
        setLinkSuccess(true);
        // Auto-close modal after brief success message (with cleanup tracking)
        successTimeoutRef.current = setTimeout(() => {
          successTimeoutRef.current = null;
          setLinkSuccess(false);
          onClose();
        }, 1500);
      } else {
        setLinkError(data.error || 'Failed to link Discord account');
      }
    };

    const handleUnlinkResult = (data: { success: boolean; error?: string }) => {
      setIsUnlinking(false);
      if (data.success) {
        setDiscordLinked(false, null);
      } else {
        setLinkError(data.error || 'Failed to unlink Discord account');
      }
    };

    socket.on(SocketEvent.DISCORD_AUTH_URL, handleAuthUrl);
    socket.on(SocketEvent.DISCORD_LINK_RESULT, handleLinkComplete);
    socket.on(SocketEvent.DISCORD_UNLINK_RESULT, handleUnlinkResult);

    return () => {
      socket.off(SocketEvent.DISCORD_AUTH_URL, handleAuthUrl);
      socket.off(SocketEvent.DISCORD_LINK_RESULT, handleLinkComplete);
      socket.off(SocketEvent.DISCORD_UNLINK_RESULT, handleUnlinkResult);
    };
  }, [socket, setDiscordLinked, onClose]);

  // Handle OAuth callback
  useEffect(() => {
    let processed = false;
    
    const processOAuthResult = (code?: string, state?: string, error?: string) => {
      if (processed) return;
      processed = true;
      
      if (error) {
        setLinkError(error);
        setIsLinking(false);
      } else if (code) {
        // Validate the state parameter for CSRF protection
        const expectedState = expectedStateRef.current || sessionStorage.getItem(OAUTH_STATE_KEY);
        
        if (!state) {
          authLogger.error('OAuth callback missing state parameter');
          setLinkError('OAuth security validation failed - missing state parameter');
          setIsLinking(false);
          return;
        }
        
        if (!expectedState) {
          authLogger.error('No expected OAuth state found - session may have expired');
          setLinkError('OAuth session expired - please try again');
          setIsLinking(false);
          return;
        }
        
        if (state !== expectedState) {
          authLogger.error('OAuth state mismatch - possible CSRF attack', { 
            receivedState: state.substring(0, 8) + '...', 
            expectedState: expectedState.substring(0, 8) + '...' 
          });
          setLinkError('OAuth security validation failed - state mismatch');
          setIsLinking(false);
          return;
        }
        
        authLogger.debug('OAuth state validated successfully');
        
        // Clear the stored state after successful validation
        expectedStateRef.current = null;
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        
        // Send the code AND state to the server for final validation
        socket?.emit(SocketEvent.DISCORD_LINK_COMPLETE, { code, state });
      }
      localStorage.removeItem('discord-oauth-result');
    };
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'discord-oauth-callback') {
        processOAuthResult(event.data.code, event.data.state, event.data.error);
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'discord-oauth-result' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          if (data.type === 'discord-oauth-callback') {
            processOAuthResult(data.code, data.state, data.error);
          }
        } catch {
          // Ignore parse errors
        }
      }
    };

    // Clear any existing poll interval at the START of the effect to prevent leaks
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    if (isLinking) {
      pollIntervalRef.current = setInterval(() => {
        const stored = localStorage.getItem('discord-oauth-result');
        if (stored) {
          try {
            const data = JSON.parse(stored);
            if (data.type === 'discord-oauth-callback' && data.timestamp && Date.now() - data.timestamp < 30000) {
              processOAuthResult(data.code, data.state, data.error);
            }
          } catch {
            // Ignore
          }
        }
      }, 500);
    }

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [socket, isLinking]);

  const handleLinkDiscord = useCallback(() => {
    setLinkError(null);
    linkDiscord();
  }, [linkDiscord]);

  const handleUnlinkDiscord = useCallback(() => {
    if (!socket) return;
    setIsUnlinking(true);
    setLinkError(null);
    socket.emit(SocketEvent.DISCORD_UNLINK);
  }, [socket]);

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} title="🎮 Discord Integration" footer={
<>
          <button className="btn btn--outline btn--slate" onClick={onClose}>
            Close
          </button>
        </>
}>
          {/* Account Linking Section */}
          <div className="d-flex flex-column gap-base">
            <h3>Discord Account</h3>
            
            {linkSuccess ? (
              <div className="link-success">
                <div className="alert alert--green">
                  <span className="success-icon">✅</span>
                  <span>Discord account linked successfully!</span>
                </div>
                <p className="field__hint">Redirecting to Discord settings...</p>
              </div>
            ) : !isLinked ? (
              <div className="d-flex flex-column gap-sm">
                <p>Link your Discord account to enable voice channel management and player linking.</p>
                {linkError && (
                  <div className="field__error">{linkError}</div>
                )}
                <button 
                  onClick={handleLinkDiscord} 
                  disabled={isLinking}
                  className="btn btn--discord"
                >
                  {isLinking ? 'Linking...' : '🔗 Link Discord Account'}
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-xs">
                <div className="d-flex align-items-center gap-base">
                  <div className="d-flex align-items-center gap-sm">
                    {discordUser?.avatar && (
                      <img
                        src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                        alt="Discord avatar"
                        className="rounded-full"
                      />
                    )}
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{discordUser?.username}</span>
                      <span className="d-flex align-items-center gap-base">✅ Linked</span>
                    </div>
                  </div>
                </div>
                {linkError && <div className="field__error">{linkError}</div>}
                <button
                  className="btn btn--red"
                  onClick={handleUnlinkDiscord}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? 'Unlinking...' : '🔗 Unlink Discord Account'}
                </button>
              </div>
            )}
          </div>
        </Modal>
  );
}
